import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Form } from 'react-bootstrap';
import EyeSvg from '../../common/components/svg/EyeSvg';
import { SALESFORCE_URLS_BY_ENVIRONMENT } from '../../common/common.entities';
import { DateTime } from 'luxon';
import { OrdersMcfTableRow } from '../../common/common.table.interfaces';
import CustomError, { CustomErrorProps } from '../../common/components/CustomError';
import RefreshSvg from '../../common/components/svg/RefreshSvg';
import { useLoading } from '../../loading/LoadingContext';
import { useUserContext } from '../../user/UserContext';
import { apiFetch } from '../../utility/fetch';
import { API_PROD_URL } from '../..';

interface WayfairMcfCompleteProps {
  handleShowContent: any;
  handleRowClicked: any
}

export default function WayfairMcfComplete({ handleShowContent, handleRowClicked }: WayfairMcfCompleteProps) {
  const { toggleLoading, changeStep, changeSubStep } = useLoading();
  const { userData } = useUserContext();

  const [ordersMcf, setOrdersMcf] = useState<OrdersMcfTableRow[]>([]);
  const [refresh, setRefresh] = useState(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS));
  const [error, setError] = useState<CustomErrorProps>({ visible: false });
  const [currentPage, setCurrentPage] = useState(1);
  const [dateFilter, setDateFilter] = useState(DateTime.now().minus({weeks: 2}).toISO())
  const [pageSize, setPageSize] = useState(10)

  const totalPages = Math.ceil(ordersMcf.length / pageSize);
  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return ordersMcf.slice(start, start + pageSize);
  }, [ordersMcf, currentPage, pageSize]);

  useEffect(() => {
    const load = async () => {
      changeStep('Wayfair Multi-Channel Fulfillments');
      changeSubStep('Loading complete orders');
      toggleLoading(true);

      const response = await apiFetch<OrdersMcfTableRow[]>({
        endpoint: 'azureTables',
        method: 'GET',
        accessToken: userData.accessToken,
        baseUrlOverride: API_PROD_URL,
        params: [
          { key: 'table', value: 'ordersMcf' },
          {
            key: 'filter',
            value:
              `(PartitionKey eq 'Wayfair-MCF-CA' or PartitionKey eq 'Wayfair-MCF-US') and status eq 'complete' and Timestamp ge datetime'${dateFilter}'`
          }
        ]
      });

      const sortedRows = response.body.sort((a, b) => {
        const dateA = DateTime.fromISO(a.timestamp);
        const dateB = DateTime.fromISO(b.timestamp);
        return dateB.toMillis() - dateA.toMillis();
      });

      setOrdersMcf(sortedRows);
      setCurrentPage(1); // Reset to first page on new data load
      toggleLoading(false);
    };

    load();
  }, [refresh, dateFilter]);

  const paginationControls = (
    <div className="mt-3 d-flex justify-content-center align-items-center gap-2 flex-wrap">
  
      <Button
        variant="secondary"
        disabled={currentPage === 1}
        onClick={() => setCurrentPage(currentPage - 1)}
      >
        Previous
      </Button>
  
      {renderPageNumbers()}
  
      <Button
        variant="secondary"
        disabled={currentPage === totalPages}
        onClick={() => setCurrentPage(currentPage + 1)}
      >
        Next
      </Button>
    </div>
  );
  
  // Helper to render page buttons with ellipses
  function renderPageNumbers() {
    const pages: (number | 'ellipsis')[] = [];
    const visibleRange = 2;
  
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
  
      if (currentPage > visibleRange + 2) {
        pages.push('ellipsis');
      }
  
      const start = Math.max(2, currentPage - visibleRange);
      const end = Math.min(totalPages - 1, currentPage + visibleRange);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
  
      if (currentPage < totalPages - visibleRange - 1) {
        pages.push('ellipsis');
      }
  
      pages.push(totalPages);
    }
  
    return pages.map((item, idx) =>
      item === 'ellipsis' ? (
        <span key={`ellipsis-${idx}`} className="mx-1 beacon-label">...</span>
      ) : (
        <Button
          key={item}
          variant={item === currentPage ? 'success' : 'outline-secondary'}
          onClick={() => setCurrentPage(item)}
        >
          {item}
        </Button>
      )
    );
  }

  return (
    <>
    <div style={{paddingBottom: '80px'}}>
      <CustomError
        closeError={() => setError({ visible: false })}
        content={error?.content}
        description={error?.description}
        title={error?.title}
        visible={error?.visible}
      />

      <div style={{height: '80px'}}>
        <span
          style={{ cursor: 'pointer' }}
          title="Refresh the table."
          onClick={() => setRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))}
        >
          <RefreshSvg height={20} />
        </span>
        <span className="beacon-label"> as of {refresh}</span>
        <span className="beacon-label"> with {ordersMcf.length} completed row(s) as of {DateTime.fromISO(dateFilter).toFormat('MMM d, yyyy')}.</span>
        <span style={{float: 'right'}}>
          <span className='beacon-label'>CHANGE FROM DATE</span>
          <Form.Control
            style={{ width: '150px', textAlign: 'center', cursor: 'pointer' }}
            type="date"
            value={dateFilter ? DateTime.fromISO(dateFilter).toFormat('yyyy-MM-dd') : ''}
            onChange={(e) => {
              const inputValue = e.target.value;
              const parsedDate = DateTime.fromFormat(inputValue, 'yyyy-MM-dd');

              if (parsedDate.isValid) {
                setDateFilter(parsedDate.toISO());
              } else {
                // Optionally handle invalid or cleared input
                setDateFilter('');
              }
            }}
          />
        </span>
        <span style={{float: 'right'}}>
          <span className='beacon-label'>PAGE SIZE</span>
          <Form.Control
            style={{width: '150px'}}
            type='number'
            value={pageSize}
            onChange={(e) => setPageSize(+e.currentTarget.value)}
          />
        </span>

      </div>

      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        <table className="flexport-table">
          <thead>
            <tr>
              <th></th>
              <th>MP ORDER NUMBER</th>
              <th>MCF ORDER NUMBER</th>
              <th>UPDATED</th>
              <th>PROCESS STATUS</th>
              <th>WAYFAIR STATUS</th>
              <th>CREATE REQUEST</th>
              <th>CREATE RESPONSE</th>
              <th>STATUS REQUEST</th>
              <th>STATUS RESPONSE</th>
              <th>TRACKING RESPONSE</th>
              <th>ERRORS</th>
            </tr>
          </thead>
          <tbody>
            {paginatedOrders.map((row, index) => (
              <tr key={index}>
                <td><span style={{cursor: 'pointer'}} className='beacon-label' onClick={() => {handleRowClicked(row)}}>{(currentPage - 1) * pageSize + index + 1} <EyeSvg height={15} /></span></td>
                <td>
                  <a
                    className="beacon-link"
                    title="View Ship Detail in Mission Control"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`${SALESFORCE_URLS_BY_ENVIRONMENT['production']}/${row.shippingDetailId}`}
                  >
                    {row.orderNumber}
                  </a>
                </td>
                <td>
                  <a
                    className="beacon-link"
                    title="View Order in Wayfair's Portal"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`https://partners.wayfair.com/d/orders/castlegate/mc/${row.fulfillmentOrderRequestId}`}
                  >
                    {row.fulfillmentOrderRequestId}
                  </a>
                </td>
                <td>{DateTime.fromISO(row.timestamp).setZone('America/Chicago').toFormat('MM/dd/yyyy t')}</td>
                <td><Badge bg="success">{row.status?.toUpperCase()}</Badge></td>
                <td><Badge bg="secondary">{row.wayfairStatus?.toUpperCase()}</Badge></td>
                <td>{renderIcon('Create Fulfillment Order Request', row.createFulfillmentOrderInput, row.orderNumber, row.fulfillmentOrderRequestId)}</td>
                <td>{row.createFulfillmentOrderResponse && renderIcon('Create Fulfillment Order Response', row.createFulfillmentOrderResponse, row.orderNumber, row.fulfillmentOrderRequestId)}</td>
                <td>{row.fulfillmentOrderDetailsInput && renderIcon('Fulfillment Order Details Request', row.fulfillmentOrderDetailsInput, row.orderNumber, row.fulfillmentOrderRequestId)}</td>
                <td>{row.fulfillmentOrderDetailsResponse && renderIcon('Fulfillment Order Details Response', row.fulfillmentOrderDetailsResponse, row.orderNumber, row.fulfillmentOrderRequestId)}</td>
                <td>{row.warehouseShippingAdviceResponse && renderIcon('Warehouse Shipping Advices Response', row.warehouseShippingAdviceResponse, row.orderNumber, row.fulfillmentOrderRequestId)}</td>
                <td>{row.errors && renderIcon('Errors', row.errors, row.orderNumber, row.fulfillmentOrderRequestId)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    <div className='sticky-bottom'>
      {paginationControls}
    </div>
    </>
    
  );

  function renderIcon(title: string, content: string, orderNumber: string, fulfillmentOrderRequestId: string) {
    return (
      <span
        title={`View the ${title}.`}
        data-title={title} 
        data-ordernumber={orderNumber}
        data-fulfillmentrequestid={fulfillmentOrderRequestId}
        data-content={content}
        className="beacon-link"
        onClick={handleShowContent}
      >
        <EyeSvg height={25} />
      </span>
    );
  }  
}
